import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Dashboard,
  People,
  Assignment,
  Logout,
  Brightness4,
  Brightness7,
  BarChart,
  AccessTime,
  CheckBox,
  CheckBoxOutlined,
  Coffee,
  Settings,
  VerifiedUser,
  Badge,
  LockReset as LockResetIcon,
  FactCheckOutlined as ApprovalsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import ChangePasswordDialog from './ChangePasswordDialog';
import logoLight from '../thumbnail_FF_Logo_V2_50X180px-01.png';
import logoDark from '../FF_Logo_V2_50X180px-02.png';

// Soft pastel palette matched to UserManagement table — keeps a user's
// avatar color consistent everywhere.
const AVATAR_PALETTE = [
  { bg: '#fee2e2', fg: '#b91c1c' },
  { bg: '#ffedd5', fg: '#c2410c' },
  { bg: '#fef3c7', fg: '#b45309' },
  { bg: '#dcfce7', fg: '#15803d' },
  { bg: '#cffafe', fg: '#0e7490' },
  { bg: '#dbeafe', fg: '#1d4ed8' },
  { bg: '#ede9fe', fg: '#6d28d9' },
  { bg: '#fce7f3', fg: '#be185d' },
];
const getAvatarPalette = (name) => {
  if (!name) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const Layout = ({ children, mode, toggleMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { canAccessModule } = usePermissions();

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Build navigation items with permission checks
  const allNavItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', alwaysShow: true, excludeRoles: ['partner'] },
    { text: 'Users', icon: <People />, path: '/users', checkPermission: true },
    { text: 'Services', icon: <Settings />, path: '/service-management', globalAdminOnly: true },
    { text: 'Roles', icon: <Badge />, path: '/role-management', globalAdminOnly: true },
    { text: 'Projects', icon: <Assignment />, path: '/projects', checkPermission: true },
    { text: 'Tasks', icon: <CheckBoxOutlined />, path: '/tasks', checkPermission: true },
    { text: 'Time & Trans.', icon: <AccessTime />, path: '/time-transactions', checkPermission: true },
    { text: 'Approvals', icon: <ApprovalsIcon />, path: '/approvals', approverOnly: true },
    { text: 'Breaks', icon: <Coffee />, path: '/break-leisure', checkPermission: true },
    { text: 'QC Review', icon: <VerifiedUser />, path: '/qc-review', qcAndAdminOnly: true },
  ];

  const navItems = allNavItems.filter(item => {
    // Exclude items for specific roles
    if (item.excludeRoles && user && item.excludeRoles.includes(user.role)) {
      return false;
    }
    
    // Always show items marked as alwaysShow
    if (item.alwaysShow) return true;
    
    // Admin-only items
    if (item.adminOnly) {
      return user && user.role === 'admin';
    }

    // Global-admin-only items (admin with no serviceid)
    if (item.globalAdminOnly) {
      return user && user.role === 'admin' && !user.serviceid;
    }
    
    // Admin or Partner only items
    if (item.adminOrPartnerOnly) {
      return user && (user.role === 'admin' || user.role === 'partner');
    }
    
    // Timesheet approvers: anyone who can be a "Reports to"
    if (item.approverOnly) {
      return user && ['admin', 'project_manager', 'team_leader', 'cmo'].includes(user.role);
    }

    // QC, Admin, and CMO only items
    if (item.qcAndAdminOnly) {
      return user && (user.role === 'admin' || user.role === 'QC' || user.role === 'cmo');
    }
    
    // Check permission-based items (modules)
    if (item.checkPermission) {
      // Team members always get read-only access to Projects even without
      // an explicit backend module permission — matches the read-only view
      // in ProjectManagement.js.
      if (item.path === '/projects' && user?.role === 'team_member') {
        return true;
      }
      const hasAccess = canAccessModule(item.path);
      console.log(`Navigation check for ${item.text} (${item.path}):`, hasAccess); // Debug
      return hasAccess;
    }
    
    return true;
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const openChangePassword = () => {
    setAnchorEl(null);
    setChangePwOpen(true);
  };

  const formatRole = (r) => {
    if (!r) return '';
    return r
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a1b1f' : '#ffffff',
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.08)'
              : '1px solid #e5e7eb',
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: { xs: 2, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Box
              component="img"
              src={mode === 'dark' ? logoDark : logoLight}
              alt="FlatForm"
              sx={{ height: 44, width: 'auto', display: 'block' }}
            />
          </Box>
          <Box
            sx={{
              width: '1px',
              alignSelf: 'stretch',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
              mx: 2,
              my: 1.5,
            }}
          />
          <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'space-around', gap: 0.5 }}>
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Button
                  key={item.text}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    fontWeight: active ? 600 : 500,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    letterSpacing: 0,
                    color: active ? 'primary.main' : 'text.secondary',
                    backgroundColor: (theme) =>
                      active
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(59,130,246,0.16)'
                          : 'rgba(59,130,246,0.08)'
                        : 'transparent',
                    borderRadius: 1.5,
                    px: 2,
                    py: 0.75,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        active
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(59,130,246,0.22)'
                            : 'rgba(59,130,246,0.12)'
                          : theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.06)'
                          : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={toggleMode}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              {mode === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
            </IconButton>
            {user && (() => {
              const pal = getAvatarPalette(user.name);
              return (
                <>
                  <Box
                    onClick={handleMenuOpen}
                    role="button"
                    tabIndex={0}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      pl: 0.75,
                      pr: 1.25,
                      py: 0.5,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      transition: 'background-color 0.12s ease',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        bgcolor: pal.bg,
                        color: pal.fg,
                        border: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.08)'
                            : '1px solid rgba(0,0,0,0.04)',
                      }}
                    >
                      {user.name?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        lineHeight: 1.15,
                      }}
                    >
                      <Box sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                        {user.name}
                      </Box>
                      <Box sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', mt: 0.25 }}>
                        {formatRole(user.role)}
                      </Box>
                    </Box>
                  </Box>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        mt: 1,
                        minWidth: 240,
                        border: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.08)'
                            : '1px solid #e5e7eb',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                        py: 0,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.25 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          bgcolor: pal.bg,
                          color: pal.fg,
                        }}
                      >
                        {user.name?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ minWidth: 0, lineHeight: 1.2 }}>
                        <Box
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {user.name}
                        </Box>
                        <Box
                          sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            mt: 0.25,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {user.email}
                        </Box>
                      </Box>
                    </Box>
                    <Divider />
                    <MenuItem
                      onClick={openChangePassword}
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        py: 0.875,
                        px: 1.5,
                        gap: 1.25,
                      }}
                    >
                      <LockResetIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      Change Password
                    </MenuItem>
                    <MenuItem
                      onClick={handleLogout}
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        py: 0.875,
                        px: 1.5,
                        gap: 1.25,
                      }}
                    >
                      <Logout sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              );
            })()}
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar sx={{ minHeight: '64px !important' }} />
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}
      >
        {children}
      </Box>
      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </Box>
  );
};

export default Layout; 